import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Download } from "lucide-react";
import { nav, site } from "@/data/content";
import { Emblem } from "@/components/Emblem";

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 border-b transition-all duration-300 ${
        scrolled
          ? "bg-fw-paper/85 backdrop-blur-xl border-fw-gold/60 shadow-sm"
          : "bg-transparent border-transparent"
      }`}
      data-testid="main-navbar"
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8 h-16 md:h-[72px] flex items-center justify-between">
        <a href="#hero" className="flex items-center gap-3" data-testid="nav-logo">
          <Emblem className="h-9 w-8" />
          <div className="leading-none">
            <p className={`font-technical uppercase font-bold tracking-wide text-sm md:text-base ${scrolled ? "text-fw-navy" : "text-white"}`}>
              FF Dingharting
            </p>
            <p className={`font-body text-[10px] tracking-[0.25em] uppercase ${scrolled ? "text-fw-red" : "text-fw-gold"}`}>
              Gegründet 1874
            </p>
          </div>
        </a>

        <nav className="hidden lg:flex items-center gap-7">
          {nav.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={`nav-link-${n.href.replace("#", "")}`}
              className={`font-technical uppercase text-sm tracking-wider transition-colors hover:text-fw-red ${
                scrolled ? "text-fw-navy/80" : "text-white/90"
              }`}
            >
              {n.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <a
            href={site.flyerPdf}
            download
            data-testid="download-pdf-button"
            className="hidden sm:inline-flex items-center gap-2 rounded-full bg-fw-blue px-5 py-2.5 text-white font-technical uppercase text-xs tracking-widest shadow-lg transition-all hover:bg-fw-red hover:shadow-xl active:scale-95"
          >
            <Download className="h-4 w-4" /> Flyer
          </a>
          <button
            onClick={() => setOpen(true)}
            className={`lg:hidden p-2 ${scrolled ? "text-fw-navy" : "text-white"}`}
            data-testid="mobile-menu-open"
            aria-label="Menü öffnen"
          >
            <Menu className="h-6 w-6" />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-fw-navy lg:hidden"
            data-testid="mobile-menu"
          >
            <div className="flex items-center justify-between px-5 h-16 border-b border-white/10">
              <span className="font-technical uppercase text-white tracking-wide">Menü</span>
              <button onClick={() => setOpen(false)} className="p-2 text-white" data-testid="mobile-menu-close" aria-label="Menü schließen">
                <X className="h-6 w-6" />
              </button>
            </div>
            <nav 
              className={`flex flex-col p-6 gap-1 ${
                scrolled
                  ? "bg-fw-navy/80 backdrop-blur-md"
                  : "bg-fw-navy"
              }`}
            >
              {nav.map((n, i) => (
                <motion.a
                  key={n.href}
                  href={n.href}
                  onClick={() => setOpen(false)}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className="font-technical uppercase text-2xl text-white/90 py-3 border-b border-white/10 hover:text-fw-gold"
                  data-testid={`mobile-nav-link-${n.href.replace("#", "")}`}
                >
                  {n.label}
                </motion.a>
              ))}
              <a
                href={site.flyerPdf}
                download
                onClick={() => setOpen(false)}
                className="mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-fw-red px-6 py-4 text-white font-technical uppercase tracking-widest"
                data-testid="mobile-download-pdf"
              >
                <Download className="h-5 w-5" /> Flyer herunterladen
              </a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
